import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useMutation } from '@tanstack/react-query';
import { updateApproval } from '@/api/approvals';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
import type { InternalTeam } from '@/types/Project';

type RejectModalProps = {
  approvalId: number;
  onCancel?: () => void;
  onSubmitted?: () => void;
  title: string;
};

export function RejectHookForm({ approvalId, onCancel, onSubmitted, title }: RejectModalProps) {
  const dispatch = useAppDispatch();

  const updateApprovalMutation = useMutation(async (payload: UpdateApprovalPayload) => updateApproval(payload), {
    onSuccess: () => {
      onSubmitted?.();
      onCancel?.();
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to update Approval',
          toastType: 'failed',
        })
      );
    },
  });

  const handleSubmit = ({ note }: { assignedUser: InternalTeam; note: string }) => {
    updateApprovalMutation.mutate({
      id: approvalId,
      status: 'REJECTED',
      note,
    });
  };

  return (
    <HookForm onSubmit={handleSubmit}>
      <p className='text-center'>Are you sure you want to reject this {title} change order?</p>
      <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
      <HookTextarea name='note' wrapperClassName='mt-5' label='Rejection Note' required />
      <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
        <Button label='Cancel' btnStyle='btn-neutral' btnType='button' disabled={updateApprovalMutation.isLoading} onClick={onCancel} />
        <Button
          label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Reject'}`}
          btnStyle='btn-primary'
          btnType='submit'
          disabled={updateApprovalMutation.isLoading}
        />
      </div>
    </HookForm>
  );
}

export const RejectModal = NiceModal.create(({ approvalId, onSubmitted, title }: Omit<RejectModalProps, 'onCancel'>) => {
  const { visible, hide } = useModal();

  if (!visible) return null;

  return (
    <Modal>
      <HookFormProvider>
        <RejectHookForm title={title} approvalId={approvalId} onCancel={hide} onSubmitted={onSubmitted} />
      </HookFormProvider>
    </Modal>
  );
});
